/**
 * Extract the backend's `context` object from a rejected API error.
 *
 * The error envelope is `{ code, message, context }`. `code` and `message` already have their
 * own readers; `context` carries the structured detail a specific failure needs — the list of
 * broken validation rules on a blocked OLI push, the option keys on a rejected price, and so on.
 *
 * Reads both shapes for the same reason `get-api-error-code` does: `backend-request` rejects
 * with the unwrapped envelope when the backend sends one, and with the raw `{ status, data }`
 * from the axios interceptor otherwise — neither carries an axios `response`.
 *
 * Returns `undefined` when the error has no context, so a caller can tell "no detail" from
 * "detail that happened to be empty".
 */
export const getApiErrorContext = (err: unknown): Record<string, unknown> | undefined => {
  const unwrapped = err as
    | { context?: unknown; data?: { error?: { context?: unknown } } | null }
    | null
    | undefined;
  const context = unwrapped?.context ?? unwrapped?.data?.error?.context;
  // Arrays are objects too, and an array here would mean the envelope is malformed — a caller
  // indexing it by key would silently read `undefined` instead of failing.
  if (typeof context !== 'object' || context === null || Array.isArray(context)) return undefined;
  return context as Record<string, unknown>;
};
